import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface RentalNoticeModalProps {
  onAgree: () => void;
  onClose?: () => void;
}

const NOTICE_SECTIONS = [
  {
    id: 'qualification',
    icon: 'ri-file-certificate-line',
    color: 'text-teal-600',
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    title: '合法出租資格',
    items: [
      '您必須是該房屋的所有權人，或持有合法書面授權方可刊登出租',
      '分租套房若涉及違章隔間，出租前請先確認是否符合建管法規',
      '公寓大廈出租需先確認管理規約有無限制或禁止條款',
      '房屋若有貸款抵押，部分金融機構合約可能限制出租，請事先確認',
    ],
  },
  {
    id: 'tax',
    icon: 'ri-government-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    title: '稅務申報義務',
    items: [
      '個人出租收入屬於「租賃所得」，依法須申報所得稅，應保留相關單據',
      '每月租金收入超過一定金額可能需開立發票或繳納營業稅，建議諮詢會計師',
      '屋主提供租屋補貼相關收據時，記得保存妥當以備稅務查核',
      '逃漏稅屬違法行為，屋主須自行承擔稅務法律責任',
    ],
  },
  {
    id: 'risk',
    icon: 'ri-alarm-warning-line',
    color: 'text-red-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    title: '出租風險提示',
    items: [
      '租客篩選不當可能導致房屋損毀、拒繳租金或社區糾紛，請謹慎評估',
      '押金依法最多收取兩個月租金，超收押金屬違法行為',
      '租客搬入後若欠繳租金，依法需依民事程序處理，耗時費力，務必先審查租客信用',
      '建議在租客入住前拍照留存房屋現況，避免退租時發生損害賠償爭議',
      '考慮投保租屋相關產險，降低房屋意外損毀的財務風險',
    ],
  },
  {
    id: 'deposit',
    icon: 'ri-money-dollar-circle-line',
    color: 'text-orange-600',
    bg: 'bg-orange-50',
    border: 'border-orange-200',
    title: '押金與費用規範',
    items: [
      '押金上限為兩個月租金（民法第 425 條之 1），不得超收',
      '租期屆滿或提前終止租約，屋主應於合理期限內返還押金',
      '管理費、停車費、網路費等額外費用應在租約中明確載明',
      '水電費轉嫁方式需在租約中清楚標示，避免日後爭議',
    ],
  },
];

export default function RentalNoticeModal({ onAgree, onClose }: RentalNoticeModalProps) {
  const navigate = useNavigate();
  const [agreed, setAgreed] = useState(false);
  const [readMore, setReadMore] = useState<Record<string, boolean>>({});

  // 鎖定背景滾動，避免手機版背景頁面滑動
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const toggleSection = (id: string) =>
    setReadMore((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleClose = () => {
    if (onClose) onClose();
    else navigate('/');
  };

  return (
    <div className="fixed inset-0 z-[9990] bg-white flex flex-col overflow-hidden">
      {/* ── 頂部 Header ── */}
      <div className="flex-shrink-0 z-10 bg-white border-b border-gray-100 px-4 py-3 flex items-center gap-3">
        {/* 左上角返回首頁 */}
        <button
          type="button"
          onClick={handleClose}
          className="w-9 h-9 flex items-center justify-center rounded-xl hover:bg-gray-100 transition-colors cursor-pointer flex-shrink-0"
          aria-label="返回首頁"
        >
          <i className="ri-arrow-left-line text-gray-600 text-lg"></i>
        </button>
        <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-xl flex-shrink-0">
          <i className="ri-file-warning-line text-teal-600 text-lg"></i>
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-base font-black text-gray-900 leading-tight">個人出租須知</h1>
          <p className="text-xs text-gray-400 mt-0.5">請仔細閱讀後同意，才可開始刊登</p>
        </div>
        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 rounded-full border border-teal-200 flex-shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span>
          <span className="text-xs font-bold text-teal-700">必讀</span>
        </div>
      </div>

      {/* ── 內容主體（唯一可捲動區域）── */}
      <div
        className="flex-1 overflow-y-auto px-4 py-5 space-y-4 max-w-2xl mx-auto w-full"
        style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
      >

        {/* 醒目提示語 */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl px-5 py-4 flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center bg-teal-500/20 rounded-xl flex-shrink-0 mt-0.5">
            <i className="ri-home-heart-line text-teal-400 text-lg"></i>
          </div>
          <div>
            <p className="text-white font-bold text-sm leading-snug mb-1">
              歡迎使用藍舍 · 個人出租平台
            </p>
            <p className="text-white/60 text-xs leading-relaxed">
              在您開始刊登房源之前，請確認您已了解個人出租的相關法規與責任，確保出租過程合法、安全、有保障。
            </p>
          </div>
        </div>

        {/* 各項須知 */}
        {NOTICE_SECTIONS.map((section) => (
          <div key={section.id} className={`rounded-2xl border ${section.border} overflow-hidden`}>
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className={`w-full flex items-center gap-3 px-4 py-3.5 ${section.bg} cursor-pointer`}
            >
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg bg-white flex-shrink-0`}>
                <i className={`${section.icon} ${section.color} text-base`}></i>
              </div>
              <span className="font-bold text-sm text-gray-800 flex-1 text-left">{section.title}</span>
              <i className={`text-gray-400 text-base transition-transform ${readMore[section.id] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
            </button>

            {readMore[section.id] && (
              <div className="px-4 py-3 space-y-2.5 bg-white">
                {section.items.map((item, idx) => (
                  <div key={idx} className="flex items-start gap-2.5">
                    <div className={`w-4 h-4 flex items-center justify-center rounded-full ${section.bg} flex-shrink-0 mt-0.5`}>
                      <i className={`ri-arrow-right-s-line ${section.color} text-xs`}></i>
                    </div>
                    <p className="text-xs text-gray-700 leading-relaxed">{item}</p>
                  </div>
                ))}
              </div>
            )}

            {!readMore[section.id] && (
              <div className="px-4 py-2.5 bg-white">
                <p className="text-xs text-gray-400 flex items-center gap-1">
                  <i className="ri-eye-line text-xs"></i>
                  點擊展開查看詳細說明（共 {section.items.length} 項）
                </p>
              </div>
            )}
          </div>
        ))}

        {/* ★★★ 租約公證 特別強調 ★★★ */}
        <div className="rounded-2xl overflow-hidden border-2 border-amber-300">
          <div className="bg-gradient-to-r from-amber-400 to-orange-400 px-4 py-3 flex items-center gap-2.5">
            <div className="w-8 h-8 flex items-center justify-center bg-white/25 rounded-xl flex-shrink-0">
              <i className="ri-verified-badge-line text-white text-lg"></i>
            </div>
            <div>
              <p className="text-white font-black text-sm leading-tight">強烈建議 · 租約公證</p>
              <p className="text-white/80 text-xs mt-0.5">最有效的法律保障措施</p>
            </div>
            <div className="ml-auto flex-shrink-0 px-2.5 py-1 bg-white/20 rounded-full">
              <span className="text-xs font-black text-white">強烈建議</span>
            </div>
          </div>

          <div className="bg-amber-50 px-4 py-4 space-y-3">
            <div className="flex items-start gap-3">
              <i className="ri-information-fill text-amber-500 text-base flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-amber-800 leading-relaxed font-medium">
                租約公證是由公證人（法院或民間公證人）確認租約內容合法有效，並賦予其強制執行力的法律程序。
              </p>
            </div>

            <div className="grid grid-cols-1 gap-2">
              {[
                { icon: 'ri-shield-check-line', title: '法律保障更完整', desc: '公證後的租約如有違約，可直接聲請強制執行，不必提起訴訟，省時省力' },
                { icon: 'ri-money-dollar-circle-line', title: '押金糾紛有依據', desc: '公證書明確記載押金金額與返還條件，雙方均有法律保障，不怕賴帳' },
                { icon: 'ri-user-received-line', title: '驅逐違約租客更快', desc: '租客欠租或毀損房屋時，持公證書可向法院聲請強制執行，比一般訴訟快許多' },
                { icon: 'ri-building-line', title: '費用低廉划算', desc: '公證費用依租金計算，通常僅數千元，相比日後糾紛訴訟費用，投資報酬率極高' },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 bg-white rounded-xl px-3.5 py-3 border border-amber-100">
                  <div className="w-7 h-7 flex items-center justify-center bg-amber-100 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-amber-600 text-sm`}></i>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-gray-800 mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-start gap-2.5 bg-amber-100 rounded-xl px-3.5 py-3">
              <i className="ri-map-pin-2-line text-amber-600 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-amber-800 leading-relaxed">
                <strong>如何辦理：</strong>攜帶雙方身分證件、房屋所有權狀及簽妥的租約，至各地地方法院公證處或民間公證人事務所辦理，通常當天可完成。
              </p>
            </div>
          </div>
        </div>

        {/* 平台聲明 */}
        <div className="bg-stone-50 rounded-2xl border border-stone-200 px-4 py-4 space-y-2">
          <p className="text-xs font-bold text-gray-600 flex items-center gap-2">
            <i className="ri-file-text-line text-gray-500"></i>平台責任聲明
          </p>
          <div className="space-y-1.5">
            {[
              '本平台為媒合服務，不介入任何租賃契約、糾紛調解或法律事宜',
              '房源資訊之真實性由屋主自行負責，平台不代為審查',
              '本平台目前為 Beta 測試版本，功能持續優化中',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-2">
                <i className="ri-arrow-right-s-line text-gray-400 flex-shrink-0 mt-0.5 text-sm"></i>
                <p className="text-xs text-gray-500 leading-relaxed">{text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 底部間距，避免被固定底欄遮住 */}
        <div className="h-4"></div>
      </div>

      {/* ── 底部操作區（非 fixed，flex-shrink-0 黏在最底） ── */}
      <div className="flex-shrink-0 bg-white border-t border-gray-100 px-4 py-4 max-w-2xl mx-auto w-full">
        {/* 展開所有提示 */}
        <div className="mb-3 flex items-center gap-2 px-3 py-2.5 bg-stone-50 rounded-xl border border-stone-200">
          <i className="ri-information-line text-gray-400 text-sm flex-shrink-0"></i>
          <p className="text-xs text-gray-500 leading-snug">
            請點擊上方各項須知展開閱讀，確認了解後再勾選同意
          </p>
        </div>

        {/* 同意勾選 */}
        <label className="flex items-start gap-3 mb-3 cursor-pointer select-none group">
          <div
            className={`mt-0.5 w-5 h-5 flex-shrink-0 rounded-md border-2 flex items-center justify-center transition-all ${
              agreed
                ? 'bg-teal-600 border-teal-600'
                : 'border-gray-300 group-hover:border-teal-400'
            }`}
            onClick={() => setAgreed(!agreed)}
          >
            {agreed && <i className="ri-check-line text-white text-xs"></i>}
          </div>
          <p className="text-xs text-gray-600 leading-relaxed" onClick={() => setAgreed(!agreed)}>
            我已閱讀並了解上述個人出租須知、風險提示及平台聲明，確認本人具備合法出租資格，並強烈建議辦理<strong className="text-amber-600">租約公證</strong>以保障雙方權益。
          </p>
        </label>

        {/* 同意按鈕 */}
        <button
          type="button"
          disabled={!agreed}
          onClick={onAgree}
          className="w-full py-3.5 bg-teal-600 text-white rounded-full font-bold text-sm hover:bg-teal-700 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap active:scale-95"
        >
          <i className="ri-check-double-line text-base"></i>
          同意並開始刊登房源
        </button>
        <p className="text-center text-xs text-gray-400 mt-2">
          點擊同意即表示您已充分了解出租須知
        </p>
      </div>
    </div>
  );
}
